import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Exam, ExamsApi } from '../api/exams'

/**
 * Settings > Exam Settings: which Exams students can reach, every Brand's on one page.
 *
 * Under the cog rather than under an Exam, because turning an Exam off is not work on that Exam:
 * it takes it out of the App for every student at once, and a checkbox among its Sittings was
 * one stray click from doing so. Disabling asks first; enabling does not, since it hides nothing.
 */
export function ExamSettings({ exams }: { exams: ExamsApi }) {
  const catalogue = useQuery({ queryKey: ['exams'], queryFn: () => exams.list() })

  if (catalogue.isPending) return <p className="text-muted">Reading the Exams...</p>
  if (catalogue.isError) {
    return (
      <p role="alert" className="border-l-2 border-red-700 pl-3 text-red-700">
        {catalogue.error.message}
      </p>
    )
  }

  const enabled = catalogue.data.filter((exam) => exam.enabled).length

  return (
    <ul className="max-w-xl divide-y divide-ink-100 rounded-lg border border-ink-100 bg-card">
      {catalogue.data.map((exam) => (
        <Availability key={exam.code} exam={exam} exams={exams} last={exam.enabled && enabled === 1} />
      ))}
    </ul>
  )
}

function Availability({ exam, exams, last }: { exam: Exam; exams: ExamsApi; last: boolean }) {
  const client = useQueryClient()
  const availability = useMutation({
    mutationFn: (enabled: boolean) => exams.setAvailability(exam.code, enabled),
    onSuccess: () => client.invalidateQueries({ queryKey: ['exams'] }),
  })

  const change = (enabled: boolean) => {
    if (!enabled && !window.confirm(warning(exam, last))) return
    availability.mutate(enabled)
  }

  return (
    <li className="p-4">
      <label className="flex items-center gap-2">
        <input type="checkbox" checked={exam.enabled} disabled={availability.isPending}
          onChange={(event) => change(event.target.checked)} />
        Enable {exam.code} for students
      </label>
      <p className="mt-1 text-sm text-muted">{exam.name} &middot; {exam.brandCode}</p>
      {availability.error && <p role="alert" className="mt-2 text-red-700">{availability.error.message}</p>}
    </li>
  )
}

function warning(exam: Exam, last: boolean) {
  const gone = `Disable ${exam.code}? Students will no longer be able to open it in the App, sign up for it, or practise it.`

  return last ? `${gone} It is the only Exam enabled, so the App will show every student "Page not found".` : gone
}
